import { useState, useEffect } from "react";
import {
  useRouter,
  useNavigate,
  useParams,
  createFileRoute,
} from "@tanstack/react-router";
import { useTerm, useUpdateTerm } from "@/lib/api/terms/hooks";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { DocEditor, isDocEmpty } from "@growing/editor";
import "@growing/editor/styles.css";
import type { DocContent } from "@growing/types";
import { Header } from "@/components/shared/header";
import { PageContainer } from "@/components/shared/page-container";
import { AdminGuard } from "@/components/shared/admin-guard";
import { toast } from "sonner";

function EditTermPage() {
  const router = useRouter();
  const navigate = useNavigate();
  const { slug } = useParams({ strict: false }) as { slug: string };
  // 편집 중인 본문. null이면 아직 손대지 않은 상태라 원본을 그대로 저장한다.
  const [editedDoc, setEditedDoc] = useState<DocContent | null>(null);

  const { data: term, isLoading } = useTerm(slug);
  const updateTerm = useUpdateTerm();

  const [title, setTitle] = useState("");

  useEffect(() => {
    if (term) {
      setTitle(term.title);
    }
  }, [term]);

  const handleSubmit = async () => {
    if (!title.trim()) {
      toast.error("제목을 입력해주세요.");
      return;
    }

    const doc = editedDoc ?? term?.doc;
    if (!doc || isDocEmpty(doc)) {
      toast.error("내용을 입력해주세요.");
      return;
    }

    try {
      await updateTerm.mutateAsync({
        slug,
        data: { title, doc },
      });
      toast.success("약관이 수정되었습니다.");
      navigate({ to: "/terms" });
    } catch {
      toast.error("약관 수정에 실패했습니다.");
    }
  };

  if (isLoading) {
    return (
      <PageContainer>
        <Skeleton className="h-8 w-24 mb-8" />
        <div className="space-y-6">
          <div className="space-y-2">
            <Skeleton className="h-4 w-12" />
            <Skeleton className="h-10 w-full" />
          </div>
          <div className="space-y-2">
            <Skeleton className="h-4 w-12" />
            <Skeleton className="h-[600px] w-full rounded-lg" />
          </div>
        </div>
      </PageContainer>
    );
  }

  if (!term) {
    return (
      <PageContainer>
        <div className="flex justify-center items-center py-12">
          <p className="text-muted-foreground">약관을 찾을 수 없습니다.</p>
        </div>
      </PageContainer>
    );
  }

  return (
    <AdminGuard>
      <PageContainer>
        <Header title="약관 수정" />

        <div className="flex flex-col gap-6">
          <div className="space-y-6">
            {/* 제목 */}
            <div className="space-y-2">
              <Label htmlFor="title">제목</Label>
              <Input
                id="title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="제목을 입력하세요"
              />
            </div>

            {/* 에디터 */}
            <div className="space-y-2">
              <Label>내용</Label>
              {/*
                약관은 adapter를 넘기지 않아 이미지를 넣을 수 없다.
                비로그인 사용자(회원가입 흐름)도 읽어야 하는데 이미지 조회 URL 발급은
                인증이 필요하기 때문이다.
              */}
              <DocEditor
                key={term.slug}
                value={term.doc}
                placeholder="약관 내용을 입력하세요"
                imageUploadDisabledMessage="약관에는 이미지를 넣을 수 없습니다."
                onChange={({ doc: next }) => setEditedDoc(next)}
                onError={(message) => toast.error(message)}
              />
            </div>

            {/* 버튼 */}
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => router.history.back()}>
                취소
              </Button>
              <Button onClick={handleSubmit} disabled={updateTerm.isPending}>
                {updateTerm.isPending ? "수정 중..." : "수정하기"}
              </Button>
            </div>
          </div>
        </div>
      </PageContainer>
    </AdminGuard>
  );
}

export const Route = createFileRoute("/_content/terms/$slug/edit")({
  component: EditTermPage,
});
